import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import apiClient from "modules/main/services/apiClient";
import { scenarioStoreSessionsEndpoint } from "modules/main/services/endpoints";
import {
  ScenarioStoreSessionsResponse,
  StoreSessionStatus,
} from "modules/checkout/types/optimizedCart";

interface ValidateBeforeCreateCartParams {
  scenarioId: number;
  storeId?: number;
}

interface ConnectedAccountsValidationState {
  isValidating: boolean;
  disconnectedStores: StoreSessionStatus[];
  isDialogOpen: boolean;
  validationError: string | null;
  setIsDialogOpen: (open: boolean) => void;
  validateBeforeCreateCart: (params: ValidateBeforeCreateCartParams) => Promise<boolean>;
}

const MISSING_SCENARIO_ERROR = "No pudimos validar este escenario.";
const GENERIC_VALIDATION_ERROR = "No pudimos validar tus cuentas conectadas. Inténtalo nuevamente.";

const getValidationErrorMessage = (error: unknown) => {
  if (error instanceof Error && error.message.includes("404")) {
    return MISSING_SCENARIO_ERROR;
  }

  return GENERIC_VALIDATION_ERROR;
};

/**
 * Validates store account connectivity before cart creation navigation.
 * @returns State and an imperative validator that resolves true only when navigation is allowed.
 */
export const useConnectedAccountsValidation = (): ConnectedAccountsValidationState => {
  const [isValidating, setIsValidating] = useState(false);
  const [disconnectedStores, setDisconnectedStores] = useState<StoreSessionStatus[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const isValidatingRef = useRef(false);

  const validateBeforeCreateCart = useCallback(
    async ({ scenarioId, storeId }: ValidateBeforeCreateCartParams) => {
      if (isValidatingRef.current) {
        return false;
      }

      isValidatingRef.current = true;
      setIsValidating(true);
      setValidationError(null);
      setDisconnectedStores([]);

      try {
        const response = await apiClient.get<ScenarioStoreSessionsResponse>(
          scenarioStoreSessionsEndpoint(scenarioId),
        );
        const relevantStores = typeof storeId === "number"
          ? response.stores.filter((store) => store.store_id === storeId)
          : response.stores;

        if (typeof storeId === "number" && relevantStores.length === 0) {
          toast.error(MISSING_SCENARIO_ERROR);
          setValidationError(MISSING_SCENARIO_ERROR);
          return false;
        }

        const nextDisconnectedStores = relevantStores.filter((store) => !store.connected);

        if (nextDisconnectedStores.length > 0) {
          setDisconnectedStores(nextDisconnectedStores);
          setIsDialogOpen(true);
          return false;
        }

        return true;
      } catch (error) {
        const message = getValidationErrorMessage(error);
        setValidationError(message);
        toast.error(message);
        return false;
      } finally {
        isValidatingRef.current = false;
        setIsValidating(false);
      }
    },
    [],
  );

  return {
    isValidating,
    disconnectedStores,
    isDialogOpen,
    validationError,
    setIsDialogOpen,
    validateBeforeCreateCart,
  };
};
